import fetchWithPasskeyStepUp from './api/passkeyFetch';
export { ApiRequestError } from './api/apiError';
function buildArtifactFormData(payload) {
    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
        if (typeof value === 'undefined' || value === null) {
            return;
        }
        if (key === 'file' && value instanceof File) {
            formData.append('inlineFile', value, value.name);
            return;
        }
        formData.append(key, String(value));
    });
    return formData;
}
class ApiService {
    constructor(baseUrl = '/api') {
        this.baseUrl = baseUrl;
        this.cache = new Map();
        this.cacheTtlMs = 60000;
    }
    getCsrfToken() {
        if (typeof document === 'undefined') {
            return null;
        }
        const tokenMatch = document.cookie.match(/(?:^|;\s*)_csrfToken=([^;]+)/);
        return tokenMatch ? decodeURIComponent(tokenMatch[1]) : null;
    }
    async request(url, options) {
        const method = options?.method?.toUpperCase() ?? 'GET';
        const cacheKey = `${method}:${url}`;
        const now = Date.now();
        const shouldCacheGet = method === 'GET' && options?.cache !== 'no-store' && !url.startsWith('/reactions');
        if (shouldCacheGet) {
            const cached = this.cache.get(cacheKey);
            if (cached && cached.expiresAt > now) {
                return cached.value;
            }
        }
        const isFormData = typeof FormData !== 'undefined' && options?.body instanceof FormData;
        const headers = {
            ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
            ...options?.headers,
        };
        if (method !== 'GET' && method !== 'HEAD') {
            const csrfToken = this.getCsrfToken();
            if (csrfToken) {
                headers['x-csrf-token'] = csrfToken;
            }
        }
        const response = await fetchWithPasskeyStepUp(`${this.baseUrl}${url}`, {
            ...options,
            credentials: 'same-origin',
            headers,
        });
        const payload = (await response.json());
        if (shouldCacheGet) {
            this.cache.set(cacheKey, {
                expiresAt: now + this.cacheTtlMs,
                value: payload,
            });
        }
        return payload;
    }
    // Home
    async getHomeData(civic = false, view) {
        const params = new URLSearchParams();
        if (civic)
            params.set('civic', '1');
        if (view)
            params.set('view', view);
        const query = params.toString();
        return this.request(`/home${query ? `?${query}` : ''}`);
    }
    async getApplicationContext(civic = false, view) {
        const params = new URLSearchParams();
        if (civic)
            params.set('civic', '1');
        if (view)
            params.set('view', view);
        const query = params.toString();
        return this.request(`/application-context${query ? `?${query}` : ''}`);
    }
    async getInstallStatus() {
        return this.request('/install');
    }
    async restoreEmptyDatabase(payload) {
        return this.request('/install/restore', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }
    async getAboutPage(id) {
        return this.request(`/pages/about/${encodeURIComponent(id)}`);
    }
    async getAnonymousContributionConfig() {
        return this.request('/anonymous-contributions/config');
    }
    async submitAnonymousContribution(payload) {
        return this.request('/anonymous-contributions', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }
    async getAnonymousContributionStatus(id, receipt) {
        return this.request(`/anonymous-contributions/status/${encodeURIComponent(id)}?receipt=${encodeURIComponent(receipt)}`);
    }
    async listAnonymousContributions(status) {
        return this.request(`/anonymous-contributions?status=${encodeURIComponent(status)}`);
    }
    async getAnonymousContribution(id) {
        return this.request(`/anonymous-contributions/${encodeURIComponent(id)}`);
    }
    async reviewAnonymousContribution(id, status, reason) {
        return this.request(`/anonymous-contributions/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            body: JSON.stringify({ status, reason }),
        });
    }
    async markAnonymousContributionPublished(id, entryType, entryId) {
        return this.request(`/anonymous-contributions/${encodeURIComponent(id)}/published`, {
            method: 'POST',
            body: JSON.stringify({ entryType, entryId }),
        });
    }
    // Topics
    async getTopics(topicId, view) {
        const params = new URLSearchParams();
        if (topicId)
            params.set('topic', topicId);
        if (view)
            params.set('view', view);
        const qs = params.toString();
        const url = qs ? `/topics?${qs}` : '/topics';
        return this.request(url);
    }
    async getTopicEntry(id, options) {
        const params = new URLSearchParams();
        if (options?.topicLink) {
            params.set('topicLink', String(options.topicLink));
        }
        if (options?.mode) {
            params.set('mode', String(options.mode));
        }
        if (options?.id) {
            params.set('id', String(options.id));
        }
        if (options?.view)
            params.set('view', options.view);
        const suffix = params.toString();
        return this.request(`/topics/entry/${id}${suffix ? `?${suffix}` : ''}`);
    }
    async createTopic(payload) {
        return this.request('/topics', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }
    async updateTopic(id, payload) {
        return this.request(`/topics/entry/${id}`, {
            method: 'PUT',
            body: JSON.stringify(payload),
        });
    }
    async updateTopicLink(id, payload) {
        return this.request(`/topics/links/${encodeURIComponent(id)}`, {
            method: 'PUT',
            body: JSON.stringify(payload),
        });
    }
    async deleteTopicLink(id) {
        return this.request(`/topics/links/${encodeURIComponent(id)}`, {
            method: 'DELETE',
        });
    }
    // Arguments
    async getArguments(topicId, view) {
        const params = new URLSearchParams();
        if (topicId)
            params.set('topic', topicId);
        if (view)
            params.set('view', view);
        const qs = params.toString();
        const url = qs ? `/arguments?${qs}` : '/arguments';
        return this.request(url);
    }
    async getArgumentEntry(id, options) {
        const params = new URLSearchParams();
        if (options?.argumentLink) {
            params.set('argumentLink', String(options.argumentLink));
        }
        if (options?.mode) {
            params.set('mode', String(options.mode));
        }
        if (options?.id) {
            params.set('id', String(options.id));
        }
        if (options?.view)
            params.set('view', options.view);
        const suffix = params.toString();
        return this.request(`/arguments/entry/${id}${suffix ? `?${suffix}` : ''}`);
    }
    async createArgument(payload) {
        return this.request('/arguments', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }
    async updateArgument(id, payload) {
        return this.request(`/arguments/entry/${id}`, {
            method: 'PUT',
            body: JSON.stringify(payload),
        });
    }
    async updateArgumentLink(id, payload) {
        return this.request(`/arguments/links/${encodeURIComponent(id)}`, {
            method: 'PUT',
            body: JSON.stringify(payload),
        });
    }
    async deleteArgumentLink(id) {
        return this.request(`/arguments/links/${encodeURIComponent(id)}`, {
            method: 'DELETE',
        });
    }
    // Questions
    async getQuestions(topicId, view) {
        const params = new URLSearchParams();
        if (topicId)
            params.set('topic', topicId);
        if (view)
            params.set('view', view);
        const qs = params.toString();
        const url = qs ? `/questions?${qs}` : '/questions';
        return this.request(url);
    }
    async getQuestionEntry(id, view) {
        return this.request(`/questions/entry/${id}${view ? `?view=${encodeURIComponent(view)}` : ''}`);
    }
    async createQuestion(payload) {
        return this.request('/questions', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }
    async updateQuestion(id, payload) {
        return this.request(`/questions/entry/${id}`, {
            method: 'PUT',
            body: JSON.stringify(payload),
        });
    }
    // Issues
    async getIssues(topicId, view) {
        const params = new URLSearchParams();
        if (topicId)
            params.set('topic', topicId);
        if (view)
            params.set('view', view);
        const qs = params.toString();
        const url = qs ? `/issues?${qs}` : '/issues';
        return this.request(url);
    }
    async getIssueEntry(id, view) {
        return this.request(`/issues/entry/${id}${view ? `?view=${encodeURIComponent(view)}` : ''}`);
    }
    async createIssue(payload) {
        return this.request('/issues', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }
    async updateIssue(id, payload) {
        return this.request(`/issues/entry/${id}`, {
            method: 'PUT',
            body: JSON.stringify(payload),
        });
    }
    // Opinions
    async getOpinions(topicId, view, classification) {
        const params = new URLSearchParams();
        if (topicId)
            params.set('topic', topicId);
        if (view)
            params.set('view', view);
        if (classification && classification !== 'all')
            params.set('classification', classification);
        const qs = params.toString();
        const url = qs ? `/opinions?${qs}` : '/opinions';
        return this.request(url);
    }
    async getOpinionEntry(id, view) {
        return this.request(`/opinions/entry/${id}${view ? `?view=${encodeURIComponent(view)}` : ''}`);
    }
    async createOpinion(payload) {
        return this.request('/opinions', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }
    async updateOpinion(id, payload) {
        return this.request(`/opinions/entry/${id}`, {
            method: 'PUT',
            body: JSON.stringify(payload),
        });
    }
    // Answers
    async getAnswers(questionId, view) {
        const params = new URLSearchParams();
        if (questionId)
            params.set('question', questionId);
        if (view)
            params.set('view', view);
        const qs = params.toString();
        const url = qs ? `/answers?${qs}` : '/answers';
        return this.request(url);
    }
    async getAnswerEntry(id, view) {
        return this.request(`/answers/entry/${id}${view ? `?view=${encodeURIComponent(view)}` : ''}`);
    }
    async createAnswer(payload) {
        return this.request('/answers', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }
    async updateAnswer(id, payload) {
        return this.request(`/answers/entry/${id}`, {
            method: 'PUT',
            body: JSON.stringify(payload),
        });
    }
    // Artifacts
    async getArtifacts(topicId, view) {
        const params = new URLSearchParams();
        if (topicId)
            params.set('topic', topicId);
        if (view)
            params.set('view', view);
        const query = params.toString();
        const url = query ? `/artifacts?${query}` : '/artifacts';
        return this.request(url);
    }
    async getArtifactEntry(id, view) {
        return this.request(`/artifacts/entry/${id}${view ? `?view=${encodeURIComponent(view)}` : ''}`);
    }
    async createArtifact(payload) {
        return this.request('/artifacts', {
            method: 'POST',
            body: buildArtifactFormData(payload),
        });
    }
    async updateArtifact(id, payload) {
        return this.request(`/artifacts/entry/${id}`, {
            method: 'PUT',
            body: buildArtifactFormData(payload),
        });
    }
    async getEntryChildren(objectName, id, view = 'wiki') {
        const encodedId = encodeURIComponent(id);
        const suffix = `?view=${encodeURIComponent(view)}`;
        switch (objectName) {
            case 'topic': {
                const response = await this.request(`/topics/entry/${encodedId}${suffix}`, { cache: 'no-store' });
                return {
                    topics: response.topicChildren || response.topics || [],
                    arguments: response.arguments || [],
                    questions: response.questions || [],
                    artifacts: response.artifacts || [],
                    issues: response.issues || [],
                    opinions: response.opinions || [],
                };
            }
            case 'argument': {
                const response = await this.request(`/arguments/entry/${encodedId}${suffix}`, { cache: 'no-store' });
                return {
                    arguments: response.arguments || [],
                    questions: response.questions || [],
                    issues: response.issues || [],
                    opinions: response.opinions || [],
                };
            }
            case 'question': {
                const response = await this.request(`/questions/entry/${encodedId}${suffix}`, { cache: 'no-store' });
                return {
                    answers: response.answers || [],
                    issues: response.issues || [],
                    opinions: response.opinions || [],
                };
            }
            case 'answer': {
                const response = await this.request(`/answers/entry/${encodedId}${suffix}`, { cache: 'no-store' });
                return { issues: response.issues || [], opinions: response.opinions || [] };
            }
            case 'artifact': {
                const response = await this.request(`/artifacts/entry/${encodedId}${suffix}`, { cache: 'no-store' });
                return {
                    artifacts: response.artifacts || [],
                    arguments: response.arguments || [],
                    questions: response.questions || [],
                    issues: response.issues || [],
                    opinions: response.opinions || [],
                };
            }
            case 'issue': {
                const response = await this.request(`/issues/entry/${encodedId}${suffix}`, { cache: 'no-store' });
                return { opinions: response.opinions || [] };
            }
            case 'opinion': {
                const response = await this.request(`/opinions/entry/${encodedId}${suffix}`, { cache: 'no-store' });
                return { issues: response.issues || [], opinions: response.opinions || [] };
            }
            default:
                return {};
        }
    }
    // Reactions
    async getEntryReactions(payload) {
        const params = new URLSearchParams();
        params.set('id', payload.id);
        params.set('objectName', payload.objectName);
        if (typeof payload.objectType === 'number') {
            params.set('objectType', String(payload.objectType));
        }
        return this.request(`/reactions?${params.toString()}`);
    }
    async setEntryReaction(payload) {
        return this.request('/reactions', {
            method: 'PUT',
            body: JSON.stringify(payload),
        });
    }
    async clearEntryReaction(payload) {
        const params = new URLSearchParams();
        params.set('id', payload.id);
        params.set('objectName', payload.objectName);
        params.set('channel', payload.channel);
        if (typeof payload.objectType === 'number') {
            params.set('objectType', String(payload.objectType));
        }
        return this.request(`/reactions?${params.toString()}`, {
            method: 'DELETE',
        });
    }
    // Search
    async search(query, options) {
        const params = new URLSearchParams();
        params.set('q', query);
        if (options?.tab && options.tab !== 'all') {
            params.set('tab', options.tab);
        }
        if (options?.content) {
            params.set('content', options.content);
        }
        if (typeof options?.limit === 'number') {
            params.set('limit', String(options.limit));
        }
        if (options?.relationship && options.relationship !== 'any') {
            params.set('relationship', options.relationship);
        }
        if (options?.evidence && options.evidence !== 'all') {
            params.set('evidence', options.evidence);
        }
        if (options?.view)
            params.set('view', options.view);
        return this.request(`/search?${params.toString()}`);
    }
    async sendContactMessage(payload) {
        return this.request('/contact', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }
    // Groups
    async getGroups() {
        return this.request('/groups');
    }
    async getGroupEntry(id) {
        return this.request(`/groups/entry/${id}`);
    }
    async getGroupPosts(id, limit = 25) {
        return this.request(`/groups/entry/${id}/posts?limit=${encodeURIComponent(String(limit))}`);
    }
    async getGroupStats(id) {
        return this.request(`/groups/entry/${id}/stats`);
    }
    async createGroup(payload) {
        return this.request('/groups', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }
    async updateGroup(id, payload) {
        return this.request(`/groups/entry/${id}`, {
            method: 'PUT',
            body: JSON.stringify(payload),
        });
    }
    async joinGroup(id) {
        return this.request(`/groups/entry/${id}/members`, {
            method: 'POST',
            body: JSON.stringify({}),
        });
    }
    async leaveGroup(id, userId) {
        return this.request(`/groups/entry/${id}/members/${encodeURIComponent(userId)}`, {
            method: 'DELETE',
        });
    }
    // Members
    async getMembers() {
        return this.request('/members');
    }
    async getScreeners() {
        return this.request('/members/screeners');
    }
    async getReviewers() {
        return this.request('/members/reviewers');
    }
    async getAdministrators() {
        return this.request('/members/administrators');
    }
    async getMemberProfile(username) {
        return this.request(`/members/${username}`);
    }
    async getMemberTopics(username, limit = 50) {
        return this.request(`/members/${encodeURIComponent(username)}/topics?limit=${encodeURIComponent(String(limit))}`);
    }
    async getMemberFollowing(username) {
        return this.request(`/members/${encodeURIComponent(username)}/following`);
    }
    async getMemberContributions(username, tab = 'all') {
        const query = tab && tab !== 'all' ? `?tab=${encodeURIComponent(tab)}` : '';
        return this.request(`/members/${encodeURIComponent(username)}/contributions${query}`);
    }
    async getMemberJournal(username, tab = 'all') {
        const query = tab && tab !== 'all' ? `?tab=${encodeURIComponent(tab)}` : '';
        return this.request(`/members/${encodeURIComponent(username)}/journal${query}`);
    }
    // Backward compatibility alias for legacy diary naming.
    async getMemberDiary(username, tab = 'all') {
        return this.getMemberJournal(username, tab);
    }
    async getCurrentMemberProfile() {
        return this.request('/members/me');
    }
    async updateCurrentMemberPreferences(payload) {
        return this.request('/members/me/preferences', {
            method: 'PUT',
            body: JSON.stringify(payload),
        });
    }
    async getCurrentMemberFastSwitchStatus() {
        return this.request('/members/me/fast-switch');
    }
    async updateCurrentMemberFastSwitch(payload) {
        return this.request('/members/me/fast-switch', {
            method: 'PUT',
            body: JSON.stringify(payload),
        });
    }
    async getMemberPages(username) {
        return this.request(`/members/${encodeURIComponent(username)}/pages`);
    }
    async createMemberPage(username, payload) {
        return this.request(`/members/${encodeURIComponent(username)}/pages`, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }
    async getMemberPage(username, pageId) {
        return this.request(`/members/${encodeURIComponent(username)}/pages/${encodeURIComponent(pageId)}`);
    }
    async updateMemberPage(username, pageId, payload) {
        return this.request(`/members/${encodeURIComponent(username)}/pages/${encodeURIComponent(pageId)}`, {
            method: 'PUT',
            body: JSON.stringify(payload),
        });
    }
    async createOutlineLink(payload) {
        return this.request('/outline/link', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }
    async getOutlineTree(rootId, depth = 2, options) {
        const params = new URLSearchParams();
        if (rootId) {
            params.set('rootId', rootId);
        }
        params.set('depth', String(depth));
        if (typeof options?.ancestorDepth === 'number') {
            params.set('ancestorDepth', String(options.ancestorDepth));
        }
        if (typeof options?.childLimit === 'number') {
            params.set('childLimit', String(options.childLimit));
        }
        if (typeof options?.rootLimit === 'number') {
            params.set('rootLimit', String(options.rootLimit));
        }
        if (options?.view)
            params.set('view', options.view);
        return this.request(`/outline/tree?${params.toString()}`);
    }
    async searchOutlineTargets(query, options) {
        const params = new URLSearchParams();
        params.set('q', query);
        params.set('types', options?.types || 'topic,argument');
        params.set('limit', String(options?.limit || 20));
        if (options?.view)
            params.set('view', options.view);
        return this.request(`/outline/search?${params.toString()}`);
    }
}
export const apiService = new ApiService();
export default apiService;
